export type Variant = { name: string; distribution: number };

export type IAbTest = {
  id: number;
  variants: Variant[];
  title: string;
  description: string;
  cookieName?: string;
  cookieVariants?: string[];
  optimizeExperimentId?: string;
  enabled: boolean;
  urlParam?: string;
  overrideVariant?: (variant: number) => number;
};

const oneThird = 100 / 3;

/**
 * Definition of available experiments.
 *
 * When an experiment is set to "enabled: false" it will always return
 * variant 0 and it will not be send to the BusinessLayer analytics in
 * the active experiments.
 */
const experiments = {
  italianCitiesPricing: {
    id: 49,
    title: 'Pricing A/B test for Italian Cities',
    description:
      'Goal of test is to find out if our prices are too high for a selection of cities and experiences.',
    variants: [
      { name: 'Regular Price', distribution: 50 },
      { name: 'Modified Price', distribution: 50 },
    ],
    cookieName: 'dynamic-price-italian-test',
    cookieVariants: ['RegularPrice', 'ModifiedPrice'],
    enabled: false,
  },
  localPicker: {
    id: 47,
    title: 'AB test for local picker',
    description:
      'This will show or hide the localpicker feature depending on the variant',
    variants: [
      { name: 'with localpicker', distribution: 50 },
      { name: 'without localpicker', distribution: 50 },
    ],
    enabled: false,
  },
  roundedPriceCards: {
    id: 50,
    title: 'Round prices in experience cards',
    description:
      'This will test if rounding the prices in experience cards will increase conversion',
    variants: [
      { name: 'without rounding', distribution: 50 }, // old
      { name: 'with rounding', distribution: 50 }, // new
    ],
    enabled: false,
  },
  landingPageTest: {
    id: 52,
    title: 'Tours and food tours landing page test',
    description:
      'This will test three different variations of the landing page',
    variants: [
      { name: 'ueSearch', distribution: oneThird },
      { name: 'ueLandingPage', distribution: oneThird },
      { name: 'storefrontLandingpage', distribution: oneThird },
    ],
    enabled: true,
    urlParam: 'lpt',
    overrideVariant: (variant) => {
      if (variant === 1 && window.location.pathname.includes('/experiences/')) {
        return 2;
      }

      return variant;
    },
  },
  marvinTest: {
    id: 57,
    title: 'Marvin entrypoint',
    description: 'Show Marvin entrypoint on the homepage',
    variants: [
      { name: 'Default', distribution: 90 },
      { name: 'Show Marvin entrypoint', distribution: 10 },
    ],
    enabled: true,
  },
  hideTopThingsToDoSlider: {
    id: 58,
    title: 'Hide top things to do slider',
    description: 'Hide top things to do slider from the search page',
    variants: [
      { name: 'Default', distribution: 50 },
      { name: 'Hide top things to do slider', distribution: 50 },
    ],
    enabled: true,
  },
  // previous test id was 58, next test id will be 59
};

export type AbTestName = keyof typeof experiments;

export function getExperimentByName(
  experimentName: AbTestName,
): IAbTest | undefined {
  return experiments[experimentName];
}

export function getExperimentById(experimentId: number): IAbTest | undefined {
  return Object.values(experiments).find(
    (experiment) => experiment.id === experimentId,
  );
}

export function getAllExperiments(): IAbTest[] {
  return Object.values(experiments);
}
